import React, {PropsWithChildren, useState} from 'react'
import {useIntl} from 'react-intl'
import {Flex, Text, useMultiStyleConfig, Box} from '@chakra-ui/react'
import {Icon, IconButton} from '_uiComponents'

interface QuantitySelectorProps {
    initialQuantity: number
    minQuantity: number
    maxQuantity: number
    onChangeQuantity: (newQuantity: number, oldQuantity: number) => void
    ariaSubject?: string
}

const QuantitySelector = ({initialQuantity, minQuantity, maxQuantity, onChangeQuantity, ariaSubject, ...props}: QuantitySelectorProps) => {
    const [quantity, setQuantity] = useState(initialQuantity)
    const {formatMessage} = useIntl()
    const styles = useMultiStyleConfig('QuantitySelector') as Record<string, PropsWithChildren>

    const handleIncrease = () => {
        const newQuantity = quantity + 1

        if (newQuantity <= maxQuantity) {
            setQuantity(newQuantity)
            onChangeQuantity(newQuantity, quantity)
        }
    }

    const handleDecrease = () => {
        const newQuantity = quantity - 1

        if (newQuantity >= minQuantity) {
            setQuantity(newQuantity)
            onChangeQuantity(newQuantity, quantity)
        }
    }

    return (
        <Flex {...styles.wrapper} {...props} data-testid="quantity-selector">
            <Box>
                <IconButton
                    variant="iconButtonBgTransparent"
                    size="iconButtonSm"
                    icon={<Icon name="IconMinus" size="sm" />}
                    onClick={handleDecrease}
                    aria-label={formatMessage({id: 'button.decrease', defaultMessage: 'Decrease quantity'}) + `, ${ariaSubject}`}
                    disabled={quantity === minQuantity}
                    data-testid="quantity-selector-decrease-button"
                />
            </Box>
            <Text as="span" {...styles.quantity} data-testid="quantity-selector-quantity">
                {quantity}
            </Text>
            <Box>
                <IconButton
                    variant="iconButtonBgTransparent"
                    size="iconButtonSm"
                    icon={<Icon name="IconPlus" size="sm" />}
                    onClick={handleIncrease}
                    aria-label={formatMessage({id: 'button.increase', defaultMessage: 'Increase quantity'}) + `, ${ariaSubject}`}
                    disabled={quantity === maxQuantity}
                    data-testid="quantity-selector-increase-button"
                />
            </Box>
        </Flex>
    )
}

export {QuantitySelector}
